import * as THREE from "three";

import parameterValues from "./parameterValues";
import globals from "./globals";
import {
  displayUniforms,
  passthroughUniforms,
  simulationUniforms,
} from "./uniforms";
import {
  displayMaterial,
  passthroughMaterial,
  maskInjectMaterial,
  simulationMaterial,
} from "./materials";
import { app } from "./context";

/** Canvas font string: Google Fonts (index.html) + local @font-face in style.css. */
function canvasFont(sizePx, family) {
  if (family === "Gulax") {
    return `normal 400 ${sizePx}px Gulax`;
  }
  if (family === "PicNic") {
    return `normal 400 ${sizePx}px PicNic`;
  }
  if (family === "Google Sans Code") {
    return `normal 400 ${sizePx}px "Google Sans Code"`;
  }
  if (family === "Syne Mono") {
    return `normal 400 ${sizePx}px "Syne Mono"`;
  }
  const name = /\s/.test(family) ? `"${family}"` : family;
  return `normal ${sizePx}px ${name}`;
}

let bufferImage, bufferCanvasCtx;
let maskCanvas, maskCtx;
let maskCanvasTexture = null;
let maskSupersampleCanvas, maskSupersampleCtx;

export const InitialTextureTypes = {
  CIRCLE: 0,
  SQUARE: 1,
  TEXT: 2,
  IMAGE: 3,
  EMPTY: 4,
  RANDOM_POINTS: 5,
};

export function drawFirstFrame(type = InitialTextureTypes.RANDOM_POINTS) {
  globals.pendingMaskInject = false;

  const bufferCanvas = document.querySelector("#buffer-canvas");
  bufferCanvasCtx = bufferCanvas.getContext("2d");
  bufferImage = document.querySelector("#buffer-image");

  bufferCanvasCtx.fillStyle = "#fff";
  bufferCanvasCtx.fillRect(
    0,
    0,
    parameterValues.canvas.width,
    parameterValues.canvas.height,
  );

  setupMaskCanvas();

  const centerX = parameterValues.canvas.width / 2;
  const centerY = parameterValues.canvas.height / 2;

  switch (type) {
    case InitialTextureTypes.CIRCLE:
      bufferCanvasCtx.beginPath();
      bufferCanvasCtx.arc(
        centerX,
        centerY,
        parameterValues.seed.circle.radius,
        0,
        Math.PI * 2,
      );
      bufferCanvasCtx.fillStyle = "#000";
      bufferCanvasCtx.fill();
      drawObstacleTextMask();
      renderInitialDataToRenderTargets(convertPixelsToTextureData());
      break;

    case InitialTextureTypes.RANDOM_POINTS: {
      const w = parameterValues.canvas.width;
      const h = parameterValues.canvas.height;
      const { minCount, maxCount, radius } = parameterValues.seed.randomPoints;
      const n =
        minCount + Math.floor(Math.random() * (maxCount - minCount + 1));
      bufferCanvasCtx.fillStyle = "#000";
      if (n > 0) {
        bufferCanvasCtx.beginPath();
        bufferCanvasCtx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        bufferCanvasCtx.fill();
        for (let i = 1; i < n; i++) {
          bufferCanvasCtx.beginPath();
          bufferCanvasCtx.arc(
            Math.random() * w,
            Math.random() * h,
            radius,
            0,
            Math.PI * 2,
          );
          bufferCanvasCtx.fill();
        }
      }
      drawObstacleTextMask();
      renderInitialDataToRenderTargets(convertPixelsToTextureData());
      break;
    }

    case InitialTextureTypes.SQUARE:
      bufferCanvasCtx.fillStyle = "#000";
      bufferCanvasCtx.translate(
        parameterValues.canvas.width / 2,
        parameterValues.canvas.height / 2,
      );
      bufferCanvasCtx.rotate(
        (parameterValues.seed.square.rotation * Math.PI) / 180,
      );
      bufferCanvasCtx.translate(
        -parameterValues.canvas.width / 2,
        -parameterValues.canvas.height / 2,
      );
      bufferCanvasCtx.fillRect(
        centerX - parameterValues.seed.square.width / 2,
        centerY - parameterValues.seed.square.height / 2,
        parameterValues.seed.square.width,
        parameterValues.seed.square.height,
      );
      bufferCanvasCtx.resetTransform();
      drawObstacleTextMask();
      renderInitialDataToRenderTargets(convertPixelsToTextureData());
      break;

    case InitialTextureTypes.TEXT:
      bufferCanvasCtx.fillStyle = "#000";
      bufferCanvasCtx.font = canvasFont(
        parameterValues.seed.text.size,
        parameterValues.mask.fontFamily,
      );
      bufferCanvasCtx.textAlign = "center";
      bufferCanvasCtx.translate(
        parameterValues.canvas.width / 2,
        parameterValues.canvas.height / 2,
      );
      bufferCanvasCtx.rotate(
        (parameterValues.seed.text.rotation * Math.PI) / 180,
      );
      bufferCanvasCtx.translate(
        -parameterValues.canvas.width / 2,
        -parameterValues.canvas.height / 2,
      );
      bufferCanvasCtx.fillText(
        parameterValues.seed.text.value,
        centerX,
        centerY,
      );
      bufferCanvasCtx.resetTransform();
      drawObstacleTextMask();
      renderInitialDataToRenderTargets(convertPixelsToTextureData());
      break;

    case InitialTextureTypes.IMAGE:
      if (parameterValues.seed.image.image != null) {
        getImagePixels(parameterValues.seed.image.image, centerX, centerY)
          .then((initialData) => {
            renderInitialDataToRenderTargets(initialData);
          })
          .catch((error) => console.error(error));
      } else {
        alert("Upload an image using the button first!");
      }
      break;

    case InitialTextureTypes.EMPTY:
      bufferCanvasCtx.clearRect(
        0,
        0,
        parameterValues.canvas.width,
        parameterValues.canvas.height,
      );
      renderInitialDataToRenderTargets(convertPixelsToTextureData());
      globals.pendingMaskInject = !!parameterValues.mask.enabled;
      break;
  }
}

export function injectObstacleMaskIntoSimulation() {
  setupMaskCanvas();
  drawObstacleTextMask();
  const tex = ensureMaskCanvasTexture();
  maskInjectMaterial.uniforms.maskTexture.value = tex;

  const src = globals.currentRenderTargetIndex;
  const dst = 1 - src;
  const { displayMesh, renderer, scene, camera, renderTargets } = app;

  displayMesh.material = maskInjectMaterial;
  renderer.setRenderTarget(renderTargets[dst]);
  renderer.render(scene, camera);

  globals.currentRenderTargetIndex = dst;
  simulationUniforms.previousIterationTexture.value =
    renderTargets[dst].texture;
  displayUniforms.textureToDisplay.value = renderTargets[dst].texture;

  displayMesh.material = simulationMaterial;
  renderer.setRenderTarget(null);
}

function ensureMaskCanvasTexture() {
  const w = parameterValues.canvas.width;
  const h = parameterValues.canvas.height;
  if (
    maskCanvasTexture &&
    maskCanvasTexture.image.width === w &&
    maskCanvasTexture.image.height === h
  ) {
    maskCanvasTexture.needsUpdate = true;
    return maskCanvasTexture;
  }
  if (maskCanvasTexture) maskCanvasTexture.dispose();
  maskCanvasTexture = new THREE.CanvasTexture(maskCanvas);
  maskCanvasTexture.minFilter = THREE.LinearFilter;
  maskCanvasTexture.magFilter = THREE.LinearFilter;
  maskCanvasTexture.flipY = true;
  return maskCanvasTexture;
}

function setupMaskCanvas() {
  if (!maskCanvas) {
    maskCanvas = document.createElement("canvas");
    maskCtx = maskCanvas.getContext("2d");
  }
  maskCanvas.width = parameterValues.canvas.width;
  maskCanvas.height = parameterValues.canvas.height;
  maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
}

function drawObstacleTextMask() {
  drawTextMask({
    text: parameterValues.mask.text,
    size: parameterValues.mask.textSize,
    rotation: 0,
    x: parameterValues.canvas.width / 2,
    y: parameterValues.canvas.height / 2,
  });
}

function drawTextMask({
  text = "HELLO",
  size = 120,
  rotation = 0,
  x = parameterValues.canvas.width / 2,
  y = parameterValues.canvas.height / 2,
} = {}) {
  const ss = 4;
  const w = parameterValues.canvas.width;
  const h = parameterValues.canvas.height;

  if (!maskSupersampleCanvas) {
    maskSupersampleCanvas = document.createElement("canvas");
    maskSupersampleCtx = maskSupersampleCanvas.getContext("2d");
  }
  maskSupersampleCanvas.width = w * ss;
  maskSupersampleCanvas.height = h * ss;
  maskSupersampleCtx.clearRect(
    0,
    0,
    maskSupersampleCanvas.width,
    maskSupersampleCanvas.height,
  );

  maskSupersampleCtx.save();
  maskSupersampleCtx.translate(x * ss, y * ss);
  maskSupersampleCtx.rotate((rotation * Math.PI) / 180);
  maskSupersampleCtx.translate(-x * ss, -y * ss);
  maskSupersampleCtx.fillStyle = "#fff";
  maskSupersampleCtx.font = canvasFont(
    size * ss,
    parameterValues.mask.fontFamily,
  );
  maskSupersampleCtx.textAlign = "center";
  maskSupersampleCtx.textBaseline = "middle";
  maskSupersampleCtx.fillText(text, x * ss, y * ss);
  maskSupersampleCtx.restore();

  maskCtx.save();
  maskCtx.imageSmoothingEnabled = true;
  maskCtx.imageSmoothingQuality = "high";
  maskCtx.drawImage(maskSupersampleCanvas, 0, 0, w, h);
  maskCtx.restore();
}

function renderInitialDataToRenderTargets(initialData) {
  const { displayMesh, renderer, scene, camera, renderTargets } = app;

  const texture = new THREE.DataTexture(
    initialData,
    parameterValues.canvas.width,
    parameterValues.canvas.height,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  texture.flipY = true;
  texture.needsUpdate = true;

  passthroughUniforms.textureToDisplay.value = texture;
  displayMesh.material = passthroughMaterial;

  for (let i = 0; i < 2; i++) {
    renderer.setRenderTarget(renderTargets[i]);
    renderer.render(scene, camera);
  }

  texture.dispose();

  displayUniforms.textureToDisplay.value = renderTargets[0].texture;
  displayMesh.material = displayMaterial;

  renderer.setRenderTarget(null);
  renderer.render(scene, camera);
}

function getImagePixels(imageData, centerX, centerY) {
  return new Promise((resolve) => {
    bufferImage.src = imageData;

    bufferImage.addEventListener("load", () => {
      bufferCanvasCtx.translate(
        (parameterValues.canvas.width / 2) * parameterValues.seed.image.scale,
        (parameterValues.canvas.height / 2) * parameterValues.seed.image.scale,
      );
      bufferCanvasCtx.rotate(
        (parameterValues.seed.image.rotation * Math.PI) / 180,
      );
      bufferCanvasCtx.translate(
        (-parameterValues.canvas.width / 2) * parameterValues.seed.image.scale,
        (-parameterValues.canvas.height / 2) * parameterValues.seed.image.scale,
      );

      let startX, startY, width, height;

      switch (parameterValues.seed.image.fit) {
        case 0:
          startX = centerX - bufferImage.width / 2;
          startY = centerY - bufferImage.height / 2;
          width = bufferImage.width * parameterValues.seed.image.scale;
          height = bufferImage.height * parameterValues.seed.image.scale;
          break;

        case 1: {
          const widthRatio = parameterValues.canvas.width / bufferImage.width;
          const heightRatio =
            parameterValues.canvas.height / bufferImage.height;
          const bestFitRatio = Math.min(widthRatio, heightRatio);
          const scaledWidth = bufferImage.width * bestFitRatio;
          const scaledHeight = bufferImage.height * bestFitRatio;
          startX = centerX - scaledWidth / 2;
          startY = centerY - scaledHeight / 2;
          width = scaledWidth;
          height = scaledHeight;
          break;
        }

        case 2:
          startX = 0;
          startY = 0;
          width = parameterValues.canvas.width;
          height = parameterValues.canvas.height;
          break;
      }

      bufferCanvasCtx.drawImage(bufferImage, startX, startY, width, height);
      bufferCanvasCtx.resetTransform();
      drawObstacleTextMask();
      resolve(convertPixelsToTextureData());
    });
  });
}

function convertPixelsToTextureData() {
  const seedPixels = bufferCanvasCtx.getImageData(
    0,
    0,
    parameterValues.canvas.width,
    parameterValues.canvas.height,
  ).data;
  const maskPixels = maskCtx.getImageData(
    0,
    0,
    parameterValues.canvas.width,
    parameterValues.canvas.height,
  ).data;
  const data = new Float32Array(seedPixels.length);
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 1.0;
    data[i + 1] = seedPixels[i + 1] === 0 ? 0.5 : 0.0;
    data[i + 2] = maskPixels[i] / 255;
    data[i + 3] = 0.0;
  }
  return data;
}
